import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { PlanogramItemSearcher } from "src/components/PlanogramExtensions/PlanogramItemSearcher";
import {
  planogramKonvaRendererAtom,
  planogramNewRendererAtom,
  planogramSelectedItemsAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Activity, Button, Color, Flex, Toggle, VerticalDivider } from "src/elements";
import { useModifiedPog } from "src/hooks/useModifiedPogAtom";
import { useTranslation } from "src/i18n";
import { KeyCode } from "src/utils/keys";
import { usePogAtomActions } from "../../store/actions";
import { getBoundaryShelvesPerBay, resolvePlanogramShelves } from "../../store/utils";
import { Tooltip } from "src/components/Tooltip/Tooltip";
import { planogram3dCameraStyleAtom } from "src/components/Planogram3d/store/atoms";
import { useShowDividers } from "src/components/Planogram/common/shelfDividersView";
import { isPlanogramItemSelection } from "src/components/Planogram/common/planogramSelection";
import { cdtSnakingInferenceRequestAtom } from "src/components/Planogram/store/cdtPlanogramEditorSessionAtoms";
import {
  getRedoPogHistoryTransition,
  getUndoPogHistoryTransition,
  PogHistoryEntry,
} from "src/components/Planogram/store/snakingHistory";

interface Props {
  pogAtom: PrimitiveAtom<Pog | null>;
  showCanvasRenderer?: boolean;
  showProducts?: boolean;
  showSnaking?: boolean;
}

const shouldBlockPlanogramShortcuts = (target: EventTarget | null) => {
  if (
    typeof document !== "undefined" &&
    document.querySelector('[role="dialog"][aria-modal="true"]')
  ) {
    return true;
  }

  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "select" ||
    tagName === "textarea" ||
    target.isContentEditable ||
    target.closest("[contenteditable]") !== null
  );
};

export const PlanogramToolbar = ({
  pogAtom,
  showCanvasRenderer = false,
  showProducts = true,
  showSnaking = false,
}: Props) => {
  const { t } = useTranslation("common");
  const { pog, setPog } = useModifiedPog(pogAtom);
  const refModifying = useRef(false);
  const refNextHistoryEntryIsApplySnaking = useRef(false);
  const setSnakingInferenceRequest = useSetAtom(cdtSnakingInferenceRequestAtom);
  const {
    setHorizontalFacings,
    setVerticalFacings,
    unrangeItems,
    applySnaking,
    moveShelves,
    canBeMovedUp,
    canBeMovedDown,
  } = usePogAtomActions(pogAtom, {
    onApplySnaking: () => {
      refNextHistoryEntryIsApplySnaking.current = true;
    },
  });
  const [selectedItems, setSelectedItems] = useAtom(planogramSelectedItemsAtom);
  const [selectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const selectedShelves = pog ? resolvePlanogramShelves(pog, selectedShelfIds) : [];
  const selectedShelfItems = selectedItems.filter(isPlanogramItemSelection);
  const [newRenderer, setNewRenderer] = useAtom(planogramNewRendererAtom);
  const [konvaRenderer, setKonvaRenderer] = useAtom(planogramKonvaRendererAtom);
  const [cameraStyle, setCameraStyle] = useAtom(planogram3dCameraStyleAtom);
  const { showDividers, setShowDividers } = useShowDividers(pog);
  const [pogHistory, setPogHistory] = useState<PogHistoryEntry[]>([]);
  const [pogRedoHistory, setPogRedoHistory] = useState<PogHistoryEntry[]>([]);

  const onUndo = () => {
    const transition = getUndoPogHistoryTransition(pogHistory, pogRedoHistory);
    if (!transition) return;

    refModifying.current = true;
    setPogRedoHistory(transition.redoHistory);
    setPog(transition.pog);
    setPogHistory(transition.history);

    if (transition.shouldReinferSnaking) {
      setSnakingInferenceRequest({ pog: transition.pog });
    }
  };

  const onRedo = () => {
    const transition = getRedoPogHistoryTransition(pogHistory, pogRedoHistory);
    if (!transition) return;

    refModifying.current = true;
    setPog(transition.pog);
    setPogHistory(transition.history);
    setPogRedoHistory(transition.redoHistory);

    if (transition.shouldReinferSnaking) {
      setSnakingInferenceRequest({ pog: transition.pog });
    }
  };

  useEffect(() => {
    if (pog) {
      if (refModifying.current) {
        refModifying.current = false;
      } else {
        setPogHistory([
          ...pogHistory,
          {
            pog: pog.toNonCircular(),
            isApplySnaking: refNextHistoryEntryIsApplySnaking.current,
          },
        ]);
        setPogRedoHistory([]);
        refNextHistoryEntryIsApplySnaking.current = false;
      }
    }
  }, [pog]);

  // Toolbar keyboard shortcuts.
  useEffect(() => {
    if (!pog) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldBlockPlanogramShortcuts(event.target)) return;

      const controlKey = event.ctrlKey || event.metaKey;
      const shiftKey = event.shiftKey;
      const keyCode = event.code;
      const hasOnlyShelfSelection =
        selectedShelfItems.length === selectedItems.length && selectedShelfItems.length > 0;

      switch (keyCode) {
        case KeyCode._0:
        case KeyCode._1:
        case KeyCode._2:
        case KeyCode._3:
        case KeyCode._4:
        case KeyCode._5:
        case KeyCode._6:
        case KeyCode._7:
        case KeyCode._8:
        case KeyCode._9: {
          event.preventDefault();
          event.stopPropagation();
          const value = keyCode === KeyCode._0 ? 10 : Number(event.key);
          if (!hasOnlyShelfSelection) {
            return;
          }

          if (controlKey) {
            setVerticalFacings(selectedShelfItems, value);
          } else {
            setHorizontalFacings(selectedShelfItems, value);
          }
          break;
        }
        case KeyCode.A: {
          if (controlKey) {
            event.preventDefault();
            event.stopPropagation();
            setSelectedItems(pog.planogram.getAllItems());
          }
          break;
        }
        case KeyCode.Z: {
          if (controlKey && shiftKey) {
            event.preventDefault();
            event.stopPropagation();
            onRedo();
          } else if (controlKey) {
            event.preventDefault();
            event.stopPropagation();
            onUndo();
          }
          break;
        }
        case KeyCode.BACKSPACE: {
          event.preventDefault();
          event.stopPropagation();
          if (!hasOnlyShelfSelection) {
            return;
          }

          unrangeItems(selectedShelfItems);
          break;
        }
        case KeyCode.UP: {
          if (selectedShelves.length === 0) return;
          event.preventDefault();
          event.stopPropagation();

          const canMoveUp = getBoundaryShelvesPerBay(selectedShelves, "top").every(canBeMovedUp);
          if (canMoveUp) {
            moveShelves(selectedShelfIds, 1);
          }
          break;
        }
        case KeyCode.DOWN: {
          if (selectedShelves.length === 0) return;
          event.preventDefault();
          event.stopPropagation();

          const canMoveDown = getBoundaryShelvesPerBay(selectedShelves, "bottom").every(
            canBeMovedDown,
          );
          if (canMoveDown) {
            moveShelves(selectedShelfIds, -1);
          }
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [pog, selectedItems, selectedShelfIds]);

  return (
    <Flex
      width="100%"
      background={Color.lightGray}
      padding="5px"
      borderRadius="3px"
      borderColor={Color.lightGrayHover}
      justify="between"
    >
      <Flex gap="5px" flexGrow>
        <Flex gap="5px">
          <Flex>
            <Button
              name="planogram-undo"
              width="35px"
              iconLeft={{ name: "undo" }}
              isDisabled={pogHistory.length < 2}
              onClick={onUndo}
            />
            <Tooltip>Undo (Ctrl + Z)</Tooltip>
          </Flex>

          <Flex>
            <Button
              name="planogram-redo"
              width="35px"
              iconLeft={{ name: "redo" }}
              isDisabled={pogRedoHistory.length === 0}
              onClick={onRedo}
            />
            <Tooltip>Redo (Ctrl + Shift + Z)</Tooltip>
          </Flex>
        </Flex>

        {showProducts && (
          <>
            <VerticalDivider />
            <PlanogramItemSearcher />
          </>
        )}
      </Flex>

      <Flex gap="5px">
        <Activity isVisible={newRenderer}>
          <Toggle
            value={cameraStyle === "3D"}
            setValue={(value) => setCameraStyle(value ? "3D" : "FRONT")}
            offLabel="Front"
            onLabel="3D"
          />
        </Activity>

        <Toggle
          value={showDividers}
          setValue={setShowDividers}
          label={t("planogramCanvas.showDividersToggle")}
        />

        <Toggle value={newRenderer} setValue={setNewRenderer} label="3D renderer" />

        {showCanvasRenderer && (
          <Toggle
            value={konvaRenderer}
            setValue={setKonvaRenderer}
            label={t("planogramCanvas.rendererToggle")}
          />
        )}

        <VerticalDivider />

        {showSnaking && (
          <Button
            name="apply-snaking"
            color="greenSmoke"
            variant="inverted"
            dropdown={[
              { label: "Odd shelves", onClick: () => applySnaking("odd") },
              { label: "Even shelves", onClick: () => applySnaking("even") },
            ]}
          >
            Apply Snaking
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
